import _extends from '@babel/runtime/helpers/extends';
import React, { useState, useCallback, useRef } from 'react';
import { p as props } from './componentEl-fcbc301e.js';
import PropTypes from 'prop-types';
import { G as Group } from './Group-0add3181.js';
import { T as Typography } from './Typography-98688343.js';
import { B as Box } from './Box-a46c799c.js';
import { W as Wrapper } from './Wrapper-2eba1576.js';
import IconCaretRight from './IconCaretRight.js';
import IconCaretDown from './IconCaretDown.js';
import { a as addSpacingClasses, m as marginPropTypes, p as paddingPropTypes } from './spacing-0f39ba4a.js';

/**
 *
 * @param {string} summary The string for the summary
 * @param {object} data The data object to be used to replace keywords in the summary string
 * @returns Array of strings and JSX to render the final summary
 */
function convertSummary({
  summary,
  data = {}
}) {
  const replaceList = {
    '%USER%': data?.user || '',
    '%NEWDATA%': data?.newData || '',
    '%OLDDATA%': data?.oldData || ''
  };
  const regex = /%[^%]+%/g;
  const matches = [...summary.matchAll(regex)];
  const summaryArray = [];
  let lastIndex = 0;
  matches.forEach((match, i) => {
    if (i === 0 && match.index !== 0 || i > 0 && match.index > lastIndex + 1) {
      summaryArray.push( /*#__PURE__*/React.createElement(React.Fragment, {
        key: `${lastIndex}-${match.index}`
      }, summary.substring(lastIndex, lastIndex + match.index - lastIndex)));
    }

    // replace match with replacement
    summaryArray.push( /*#__PURE__*/React.createElement("strong", {
      key: `${match[0]}-${match.index}`
    }, replaceList[match[0]] || ''));
    lastIndex = match.index + match[0].length;

    // if match is last one and there is more left to the summary string
    if (i === matches.length - 1 && lastIndex < summary.length) {
      summaryArray.push(summary.substring(lastIndex));
      lastIndex = summary.length;
    }

    // if match is not the last one and there is text between this match and the next
    if (i < matches.length - 1 && matches[i + 1].index !== lastIndex) {
      summaryArray.push( /*#__PURE__*/React.createElement(React.Fragment, {
        key: `${lastIndex}-${matches[i + 1].index}`
      }, summary.substring(lastIndex, matches[i + 1].index)));
      lastIndex = matches[i + 1].index - 1;
    }
  });
  return summaryArray;
}
const ConditionalDetails = ({
  onToggle,
  isOpen,
  children,
  details
}) => {
  const detailsRef = useRef();
  if (details) return /*#__PURE__*/React.createElement("details", {
    onToggle: onToggle,
    ref: detailsRef,
    className: "st_logs__detailroot",
    open: isOpen
  }, children);
  return children;
};
ConditionalDetails.propTypes = {
  onToggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  details: PropTypes.bool.isRequired
};
const ConditionalSummary = ({
  details,
  children
}) => {
  if (details) {
    return /*#__PURE__*/React.createElement("summary", {
      className: "st_focusable st_clickable st_logsitem__summary"
    }, children);
  }
  return children;
};
ConditionalSummary.propTypes = {
  details: PropTypes.bool.isRequired
};
function LogsItem({
  componentEl: Component,
  margin,
  open,
  timestamp,
  summary,
  details,
  oldData,
  newData,
  user,
  ...otherProps
}) {
  let cssClasses = ['st_logsitem', 'st_reset--list'];
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  });
  const [isOpen, setIsOpen] = useState(open);
  const updateOnToggle = useCallback(e => {
    if (e.target.hasAttribute('open')) return setIsOpen(true);
    return setIsOpen(false);
  }, []);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), /*#__PURE__*/React.createElement(ConditionalDetails, {
    details: details,
    isOpen: isOpen,
    onToggle: updateOnToggle
  }, /*#__PURE__*/React.createElement(ConditionalSummary, {
    details: details
  }, /*#__PURE__*/React.createElement(Wrapper, {
    padding: {
      all: 'sm'
    }
  }, /*#__PURE__*/React.createElement(Group, {
    fullWidth: true,
    direction: "row",
    gap: "md"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "note"
  }, timestamp), /*#__PURE__*/React.createElement(Group.Item, {
    flex: true
  }, /*#__PURE__*/React.createElement(Typography, {
    color: "ink",
    componentEl: "div"
  }, typeof summary === 'function' && summary({
    newData,
    oldData,
    user
  }), typeof summary === 'string' && convertSummary({
    summary,
    data: {
      newData,
      oldData,
      user
    }
  }))), details && (isOpen ? /*#__PURE__*/React.createElement(IconCaretDown, {
    color: "asphalt"
  }) : /*#__PURE__*/React.createElement(IconCaretRight, {
    color: "blurple"
  }))))), details && /*#__PURE__*/React.createElement(Box, {
    padding: {
      all: 'md'
    },
    margin: {
      vertical: 'sm'
    }
  }, /*#__PURE__*/React.createElement(Group, {
    direction: "column",
    gap: "md"
  }, /*#__PURE__*/React.createElement(Group, {
    direction: "column",
    gap: "xs",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "label3"
  }, "From"), /*#__PURE__*/React.createElement(Typography, {
    color: "asphalt",
    componentEl: "div"
  }, oldData)), /*#__PURE__*/React.createElement(Group, {
    direction: "column",
    gap: "xs",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "label3"
  }, "To"), /*#__PURE__*/React.createElement(Typography, {
    componentEl: "div"
  }, newData))))));
}
LogsItem.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** margin for component, if any */
  margin: marginPropTypes,
  /** initial open state of accordion */
  open: PropTypes.bool,
  /** timestamp of event */
  timestamp: PropTypes.string.isRequired,
  /** summary of event
   * Either a string with `%USER%` `%OLDDATA%` `%NEWDATA%`
   * to be replaced with user, oldData, newData,
   * Or a function that accepts `{ user, oldData, new Data}` and returns JSX */
  summary: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  /** whether to show expandable details */
  details: PropTypes.bool,
  /** username of user that made the change */
  user: PropTypes.string.isRequired,
  /** old version of data. Must include newData to be rendered in accordion. */
  oldData: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /** new version of data. Must include oldData tobe rendered in accordion. */
  newData: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
};
LogsItem.defaultProps = {
  componentEl: 'li',
  margin: {
    all: 'none'
  },
  open: false,
  details: false,
  oldData: null,
  newData: null
};

function LogsGroup({
  componentEl: Component,
  margin,
  children,
  timestamp,
  ...otherProps
}) {
  let cssClasses = ['st_loggroup'];
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), /*#__PURE__*/React.createElement(Typography, {
    size: "sm",
    color: "asphalt",
    weight: "bold",
    componentEl: "h3"
  }, timestamp), children);
}
LogsGroup.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** margin for component, if any */
  margin: marginPropTypes,
  /** timestamp for this group of events */
  timestamp: PropTypes.string.isRequired
};
LogsGroup.defaultProps = {
  componentEl: 'div',
  margin: {
    all: 'none'
  }
};
LogsGroup.Item = LogsItem;

function Logs({
  componentEl: Component,
  margin,
  padding,
  ...otherProps
}) {
  let cssClasses = ['st_logs', 'st_reset--list'];
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    role: "separator",
    className: cssClasses.join(' ')
  }, otherProps));
}
Logs.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** margin for component, if any */
  margin: marginPropTypes,
  /** padding for component, if any */
  padding: paddingPropTypes
};
Logs.defaultProps = {
  componentEl: 'ul',
  margin: {
    all: 'none'
  },
  padding: {
    all: 'none'
  }
};
Logs.Item = LogsItem;
Logs.Group = LogsGroup;

export { Logs as L };
